<template>
  <div>
    <PageAllow controller="TemplatePriceAdjustTasks" :action="isNew ? 'add' : (isEdit ? 'edit' : 'index')" />
    <Breadcrumbs :items="items" />
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          <span v-if="isNew">
            {{ $t("TemplatePriceAdjustTasks.newInfo") }}
          </span>
          <span v-else-if="isEdit">
            {{ $t("TemplatePriceAdjustTasks.editInfo") }}
          </span>
          <span v-else>
            {{ $t("TemplatePriceAdjustTasks.viewInfo") }}
          </span>
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="$router.push('/template-price-adjust-tasks')"
              >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("TemplatePriceAdjustTasks.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <!-- 任务类型 -->
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("TemplatePriceAdjustTasks.Fields.type") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <template v-if="isNew || isEdit">
                    <v-radio-group row v-model="form.type">
                      <v-radio
                        v-for="item in taskTypeItems"
                        class="mt-n2"
                        :key="item.value"
                        :label="item.text"
                        :value="item.value"
                      ></v-radio>
                    </v-radio-group>
                  </template>
                  <span v-else>{{ form.type == 1 ? $t('TemplatePriceAdjustTasks.Fields.type_all') : $t('TemplatePriceAdjustTasks.Fields.type_sub') }}</span>
                </v-col>
              </v-row>

              <!-- 执行方式 -->
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("TemplatePriceAdjustTasks.Fields.execute_mode") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <template v-if="isNew || isEdit">
                    <v-radio-group row v-model="form.execute_mode">
                      <v-radio
                        v-for="item in executeModeItems"
                        class="mt-n2"
                        :key="item.value"
                        :label="item.text"
                        :value="item.value"
                      ></v-radio>
                    </v-radio-group>
                  </template>
                  <span v-else>{{ form.schedule_time ? $t('TemplatePriceAdjustTasks.Fields.execute_schedule') : $t('TemplatePriceAdjustTasks.Fields.execute_now') }}</span>
                </v-col>
              </v-row>

              <!-- 预约时间 -->
              <v-row align="center" v-if="form.execute_mode == 1">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("TemplatePriceAdjustTasks.Fields.schedule_time") + $t("Global.colon") }}
                  <Required :show="isNew || isEdit" mode="new" />
                </v-col>
                <v-col cols="9" md="7">
                  <template v-if="isNew || isEdit">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('TemplatePriceAdjustTasks.Fields.schedule_time')"
                      :rules="form.execute_mode == 1 ? 'required' : ''"
                    >
                      <SearcherDatetime
                        v-model="form.schedule_time"
                        type="datetime"
                        single-line
                        dense
                        hide-details
                        hide-icon
                        disabledBeforeToday
                        :required="form.execute_mode == 1"
                        :errors="errors"
                        :label="$t('TemplatePriceAdjustTasks.Fields.schedule_time')"
                      >
                        <template v-slot:message="{ key, message }">
                          <div :class="key == 0 ? 'danger--text' : ''">
                            {{ message }}
                          </div>
                        </template>
                      </SearcherDatetime>
                    </ValidationProvider>
                  </template>
                  <span v-else>
                    <template v-if="form.schedule_time">
                      {{ form.schedule_time | formatTime }}
                    </template>
                    <template v-else>-</template>
                  </span>
                </v-col>
              </v-row>

              <!-- 备注 -->
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("TemplatePriceAdjustTasks.Fields.memo") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <template v-if="isNew || isEdit">
                    <v-textarea
                      outlined
                      v-model="form.memo"
                      :label="$t('TemplatePriceAdjustTasks.Fields.memo')"
                      rows="2"
                    ></v-textarea>
                  </template>
                  <span v-else>{{ form.memo || '-' }}</span>
                </v-col>
              </v-row>

              <!-- 选择商品/模板按钮 -->
              <v-row align="center" v-if="isNew || isEdit">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("TemplatePriceAdjustTasks.Fields.task_details") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <v-btn
                    color="primary"
                    class="my-1 mr-3"
                    small
                    outlined
                    @click="materialsDialog = true"
                  >
                    {{ $t("CRUD.chooseInfo", [$t("Material.ObjectName")]) }}
                  </v-btn>
                  <v-btn
                    v-if="form.type == 2"
                    color="primary"
                    class="my-1 mr-3"
                    small
                    outlined
                    @click="templatesDialog = true"
                  >
                    {{ $t("CRUD.chooseInfo", [$t("Template.ObjectName")]) }}
                  </v-btn>
                </v-col>
              </v-row>

              <!-- 全模板表格 -->
              <v-row v-if="form.type == 1">
                <v-col cols="12" style="overflow-x: auto">
                  <v-data-table
                    :headers="fullTemplateHeaders"
                    :items="tableData"
                    multi-sort
                    class="elevation-1 px-4 pt-2"
                    disable-pagination
                    hide-default-footer
                    fixed-header
                  >
                    <template v-slot:header.actions="{ header }">
                      {{ header.text }}
                      <v-icon x-small>fa-sliders-h</v-icon>
                    </template>
                    <template v-slot:item.supply_price="{ item }">
                      <template v-if="isNew || isEdit">
                        <v-text-field
                          :value="item.supply_price"
                          type="text"
                          dense
                          hide-details
                          outlined
                          style="max-width: 200px"
                          :rules="moneyRequiredRules"
                          :prefix="$t('Global.money')"
                          @input="validatePrice(item, 'supply', $event)"
                        >
                        </v-text-field>
                      </template>
                      <span v-else>{{ item.supply_price ? ($t('Global.money') + item.supply_price) : '-' }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }" v-if="isNew || isEdit">
                      <v-btn
                        small
                        text
                        class="danger--text pa-0"
                        @click="removeMaterial(item)"
                      >
                        <v-icon>fa-times</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>

              <!-- 分模板表格 -->
              <v-row v-if="form.type == 2">
                <v-col cols="12" style="overflow-x: auto">
                  <v-data-table
                    :headers="subTemplateHeaders"
                    :items="tableData"
                    multi-sort
                    class="elevation-1 px-4 pt-2"
                    disable-pagination
                    hide-default-footer
                    fixed-header
                  >
                    <template v-slot:header.actions="{ header }">
                      {{ header.text }}
                      <v-icon x-small>fa-sliders-h</v-icon>
                    </template>
                    <!-- eslint-disable vue/valid-v-for, vue/no-template-key -->
                    <template v-for="tpl in selectedTemplates" v-slot:[tpl.slotName]="{ item }">
                      <div :key="tpl.id">
                        <v-text-field
                          v-if="(isNew || isEdit) && !item.template_disabled[tpl.id]"
                          :value="item.template_prices[tpl.id]"
                          type="text"
                          dense
                          hide-details
                          outlined
                          style="max-width: 200px"
                          :rules="moneyOptionalRules"
                          :prefix="$t('Global.money')"
                          @input="validatePrice(item, tpl.id, $event)"
                        >
                        </v-text-field>
                        <span v-else>{{ item.template_prices[tpl.id] ? ($t('Global.money') + item.template_prices[tpl.id]) : '-' }}</span>
                      </div>
                    </template>
                    <!-- eslint-enable vue/valid-v-for, vue/no-template-key -->
                    <template v-slot:item.actions="{ item }" v-if="isNew || isEdit">
                      <v-btn
                        small
                        text
                        class="danger--text pa-0"
                        @click="removeMaterial(item)"
                      >
                        <v-icon>fa-times</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </ValidationObserver>
          </v-container>
        </v-card-text>
      </v-card>

      <!-- 右侧按钮 -->
      <v-sheet class="page-opts">
        <v-card>
          <v-card-title>
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  outlined
                  color="secondary"
                  class="elevation-1"
                  block
                  @click="$router.push('/template-price-adjust-tasks')"
                >
                  <v-icon small class="pe-1">fa-times</v-icon>
                  {{ $t("CRUD.back") }}
                </v-btn>
              </v-col>
              <v-col cols="12" v-if="isNew || isEdit">
                <v-btn
                  color="success"
                  block
                  :disabled="loading"
                  @click="submit()"
                >
                  <v-icon small class="pe-1">fa-save</v-icon>
                  {{ $t("$vuetify.save") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>
      </v-sheet>
    </v-sheet>

    <!-- 选择商品弹窗 -->
    <MaterialsSelectsDialog
      v-if="isNew || isEdit"
      v-model="materialsDialog"
      :materialIds="materialIds"
      @confirm="handleMaterialsSelect"
    />

    <!-- 选择模板弹窗 -->
    <DialogTemplates
      v-if="isNew || isEdit"
      v-model="templatesDialog"
      :templateSelected="selectedTemplates"
      @confirm="handleTemplateSelect"
    />
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Required from "@/components/plugins/Required";
import SearcherDatetime from "@/components/plugins/SearcherDatetime";
import MaterialsSelectsDialog from "@/components/plugins/MaterialsSelectsDialog";
import DialogTemplates from "@/components/plugins/DialogTemplates";
import TemplatePriceAdjustTasks from "@/api/template_price_adjust_tasks";

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    ValidationObserver,
    ValidationProvider,
    Required,
    SearcherDatetime,
    MaterialsSelectsDialog,
    DialogTemplates,
  },

  computed: {
    isNew() {
      return this.mode === "new";
    },
    isView() {
      return this.mode === "view";
    },
    isEdit() {
      return this.mode === "edit";
    },
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.materialManagement"),
          disabled: false,
          href: "/materials",
        },
        {
          text: i18n.t("Nav.templatePriceAdjustTasks"),
          disabled: false,
          href: "/template-price-adjust-tasks",
        },
        {
          text: this.isNew
            ? i18n.t("TemplatePriceAdjustTasks.newInfo")
            : (this.isEdit
              ? i18n.t("TemplatePriceAdjustTasks.editInfo")
              : i18n.t("TemplatePriceAdjustTasks.viewInfo")),
          disabled: true,
          href: this.isNew
            ? "/template-price-adjust-tasks-new"
            : "/template-price-adjust-tasks/" + this.$route.params.id,
        },
      ];
    },

    // 全模板表格表头
    fullTemplateHeaders() {
      const headers = [
        {
          text: i18n.t("Material.Fields.material_code"),
          align: "start",
          sortable: false,
          value: "material_code",
        },
        {
          text: i18n.t("Material.Fields.material_name"),
          align: "start",
          sortable: false,
          value: "material_name",
        },
        {
          text: i18n.t("Material.Fields.material_category"),
          align: "start",
          sortable: false,
          value: "category_name",
        },
        {
          text: i18n.t("TemplatePriceAdjustTasks.Fields.supply_price"),
          align: "start",
          sortable: false,
          value: "supply_price",
        },
      ];
      if (this.isNew || this.isEdit) {
        headers.push({
          text: i18n.t("TableHeaders.actions"),
          align: "center",
          sortable: false,
          value: "actions",
        });
      }
      return headers;
    },

    // 分模板表格表头（动态模板列）
    subTemplateHeaders() {
      const headers = [
        {
          text: i18n.t("Material.Fields.material_code"),
          align: "start",
          sortable: false,
          value: "material_code",
        },
        {
          text: i18n.t("Material.Fields.material_name"),
          align: "start",
          sortable: false,
          value: "material_name",
        },
        {
          text: i18n.t("Material.Fields.material_category"),
          align: "start",
          sortable: false,
          value: "category_name",
        },
      ];
      // 动态添加模板列
      this.selectedTemplates.forEach((tpl) => {
        headers.push({
          text: tpl.template_name || tpl.name || "",
          align: "start",
          sortable: false,
          value: "template_" + tpl.id,
        });
      });
      if (this.isNew || this.isEdit) {
        headers.push({
          text: i18n.t("TableHeaders.actions"),
          align: "center",
          sortable: false,
          value: "actions",
        });
      }
      return headers;
    },

    // 已选商品ID列表（用于弹窗勾选和禁止取消）
    materialIds() {
      return this.tableData.map((row) => parseInt(row.material_id));
    },

    // 全模板价格校验规则：必填，≥0，最多两位小数
    moneyRequiredRules() {
      return [
        (v) => !!v || i18n.t("Global.requried"),
        (v) => /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(v) || "≥0，最多两位小数",
      ];
    },

    // 分模板价格校验规则：非必填，非空时 ≥0，最多两位小数
    moneyOptionalRules() {
      return [
        (v) => {
          if (!v) return true;
          return /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(v) || "≥0，最多两位小数";
        },
      ];
    },

    ...mapState(["settings"]),
  },

  data: () => ({
    mode: "new",
    form: {
      type: 1,
      execute_mode: 0,
      memo: "",
      schedule_time: "",
    },
    taskTypeItems: [
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.type_all"), value: 1 },
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.type_sub"), value: 2 },
    ],
    executeModeItems: [
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.execute_now"), value: 0 },
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.execute_schedule"), value: 1 },
    ],
    tableData: [],
    selectedTemplates: [],
    materialsDialog: false,
    templatesDialog: false,
    loading: false,
  }),

  mounted() {
    // 初始化 radio 项的文本（i18n 在 data 初始化时可能还未加载）
    this.taskTypeItems = [
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.type_all"), value: 1 },
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.type_sub"), value: 2 },
    ];
    this.executeModeItems = [
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.execute_now"), value: 0 },
      { text: i18n.t("TemplatePriceAdjustTasks.Fields.execute_schedule"), value: 1 },
    ];

    // 根据路由确定模式
    if (this.$route.params.mode) {
      this.mode = this.$route.params.mode;
    } else if (this.$route.params.id) {
      this.mode = "view";
    }

    // 加载任务数据（编辑/查看模式）
    if (!this.isNew) {
      this.fetchTaskData();
    }
  },

  methods: {
    // 查看模式：加载任务数据
    async fetchTaskData() {
      const id = this.$route.params.id;
      if (!id) return;
      this.loading = true;
      try {
        const res = await TemplatePriceAdjustTasks.view(id);
        if (res.data.status.isOK()) {
          const task = res.data.templatePriceAdjustTask;
          this.form.type = task.type || 1;
          this.form.schedule_time = task.schedule_time || "";
          // 根据 schedule_time 动态判定执行方式
          this.form.execute_mode = this.form.schedule_time ? 1 : 0;
          this.form.memo = task.memo || "";

          const details = task.template_price_adjust_details;
          if (!details || !details.length) {
            this.loading = false;
            return;
          }

          if (task.type == 2) {
            // 分模板模式：构建模板列
            if (task.templates && task.templates.length) {
              this.selectedTemplates = task.templates.map((tpl) => ({
                id: tpl.id,
                template_name: tpl.template_name || "",
                slotName: this.getTemplateSlotName(tpl.id),
              }));
            }

            this.tableData = details.map((detail) => {
              // 从 template_details 提取各模板价格
              const templatePrices = {};
              if (detail.template_details) {
                detail.template_details.forEach((td) => {
                  templatePrices[td.template_id] = td.supply_price || "";
                });
              }

              return {
                material_id: detail.material_id || "",
                material_code: detail.material ? detail.material.material_code : "",
                material_name: detail.material ? detail.material.material_name : "",
                category_name:
                  detail.material && detail.material.material_category
                    ? detail.material.material_category.name
                    : "",
                supply_price: detail.supply_price || "",
                template_prices: templatePrices,
                template_disabled: {},
                disabled_template_ids: new Set(),
              };
            });
          } else {
            // 全模板模式
            this.tableData = details.map((detail) => {
              return {
                material_id: detail.material_id || "",
                material_code: detail.material ? detail.material.material_code : "",
                material_name: detail.material ? detail.material.material_name : "",
                category_name:
                  detail.material && detail.material.material_category
                    ? detail.material.material_category.name
                    : "",
                supply_price: detail.supply_price || "",
                template_prices: {},
              };
            });
          }
        }
      } catch (e) {
        console.error("获取任务详情失败", e);
      }
      this.loading = false;
    },

    // 生成模板列的动态 slot 名称
    getTemplateSlotName(templateId) {
      return "item.template_" + templateId;
    },

    // 选择商品回调（多选）
    handleMaterialsSelect(materials) {
      if (!materials || !materials.length) return;
      // 过滤已添加的商品
      const newMaterials = materials.filter(
        (m) => !this.tableData.find((row) => row.material_id == m.id)
      );
      if (newMaterials.length) {
        const ids = newMaterials.map((m) => m.id);
        this.fetchMaterialsInfo(ids);
      }
    },

    // 批量获取商品详情
    async fetchMaterialsInfo(materialIds) {
      this.loading = true;
      try {
        const res = await TemplatePriceAdjustTasks.getAddItemInfo({ material_ids: materialIds });
        if (res.data.status.isOK()) {
          // 兼容数组和单个对象两种响应格式
          const infoList = Array.isArray(res.data.materials)
            ? res.data.materials
            : (res.data.material ? [res.data.material] : []);
          infoList.forEach((info) => {
            const templatePrices = {};
            const templateDisabled = {};
            // 收集 API 返回的全部 disabled 模板 ID（不依赖 selectedTemplates）
            const disabledTemplateIds = new Set();
            if (info.template_details) {
              const details = Array.isArray(info.template_details)
                ? info.template_details
                : Object.values(info.template_details);
              details.forEach((td) => {
                if (td.disabled == 1 && td.template_id != null) {
                  disabledTemplateIds.add(String(td.template_id));
                }
              });
            }
            // 以当前已选模板初始化 template_prices 和 template_disabled
            this.selectedTemplates.forEach((tpl) => {
              templatePrices[tpl.id] = "";
              templateDisabled[tpl.id] = disabledTemplateIds.has(String(tpl.id));
            });
            this.tableData.push({
              material_id: info.id || "",
              material_code: info.material_code || "",
              material_name: info.material_name || "",
              category_name: info.material_category ? info.material_category.name : "",
              supply_price: "",
              template_prices: templatePrices,
              template_disabled: templateDisabled,
              disabled_template_ids: disabledTemplateIds,
            });
          });
        }
      } catch (e) {
        console.error("获取商品详情失败", e);
      }
      this.loading = false;
    },

    // 选择模板回调
    handleTemplateSelect(templates) {
      // 为每个模板设置 slotName
      templates.forEach((tpl) => {
        if (!tpl.slotName) {
          tpl.slotName = this.getTemplateSlotName(tpl.id);
        }
      });

      // 反选模板 = 删除表格列
      const removedTemplates = this.selectedTemplates.filter(
        (oldTpl) => !templates.find((newTpl) => newTpl.id == oldTpl.id)
      );
      this.selectedTemplates = templates;

      // 更新表格数据：移除被删除的模板价格字段
      this.tableData.forEach((row) => {
        removedTemplates.forEach((tpl) => {
          delete row.template_prices[tpl.id];
          delete row.template_disabled[tpl.id];
        });
        // 添加新模板的价格字段，从行级 disabled 集合判定初始状态
        templates.forEach((tpl) => {
          if (!(tpl.id in row.template_prices)) {
            this.$set(row.template_prices, tpl.id, "");
            const isDisabled = row.disabled_template_ids
              ? row.disabled_template_ids.has(String(tpl.id))
              : false;
            this.$set(row.template_disabled, tpl.id, isDisabled);
          }
        });
      });
    },

    // 删除商品
    removeMaterial(item) {
      const index = this.tableData.findIndex(
        (row) => row.material_id == item.material_id
      );
      if (index > -1) {
        this.tableData.splice(index, 1);
      }
    },

    // 价格输入验证
    validatePrice(item, templateId, value) {
      // 移除非数字和小数点字符
      let cleaned = value.replace(/[^\d.]/g, "");
      // 确保只有一个小数点
      const dotIndex = cleaned.indexOf(".");
      if (dotIndex > -1) {
        cleaned =
          cleaned.substring(0, dotIndex + 1) +
          cleaned.substring(dotIndex + 1).replace(/\./g, "");
      }
      // 确保只有两位小数
      if (dotIndex > -1 && cleaned.length - dotIndex > 3) {
        cleaned = cleaned.substring(0, dotIndex + 3);
      }
      // 确保 >= 0
      if (cleaned !== "" && parseFloat(cleaned) < 0) {
        cleaned = "";
      }
      if (templateId === "supply") {
        item.supply_price = cleaned;
      } else {
        this.$set(item.template_prices, templateId, cleaned);
      }
    },

    // 提交保存
    async submit() {
      // 验证预约时间
      if (this.form.execute_mode == 1 && !this.form.schedule_time) {
        this.$snackbar.error(i18n.t("TemplatePriceAdjustTasks.Messages.scheduleTimeRequired"));
        return;
      }

      // 验证商品
      if (!this.tableData.length) {
        this.$snackbar.error(i18n.t("TemplatePriceAdjustTasks.Messages.materialRequired"));
        return;
      }

      // 分模板模式验证模板
      if (this.form.type == 2 && !this.selectedTemplates.length) {
        this.$snackbar.error(i18n.t("TemplatePriceAdjustTasks.Messages.templateRequired"));
        return;
      }

      // 组装提交数据
      let task_details = [];
      if (this.form.type == 1) {
        // 全模板：每行价格必填校验
        for (let i = 0; i < this.tableData.length; i++) {
          const row = this.tableData[i];
          if (!row.supply_price || !/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(row.supply_price)) {
            this.$snackbar.error(
              i18n.t("TemplatePriceAdjustTasks.Messages.priceRequired", {
                index: i + 1,
                code: row.material_code || "",
              })
            );
            return;
          }
        }
        task_details = this.tableData.map((row) => ({
          material_id: row.material_id ? row.material_id.toString() : "",
          supply_price: row.supply_price || "0",
        }));
      } else {
        // 分模板：过滤全部模板价格均为空的行
        const validRows = this.tableData.filter((row) => {
          return this.selectedTemplates.some((tpl) => {
            return row.template_prices[tpl.id] && row.template_prices[tpl.id] !== "";
          });
        });
        if (!validRows.length) {
          this.$snackbar.error(i18n.t("TemplatePriceAdjustTasks.Messages.materialRequired"));
          return;
        }
        task_details = validRows.map((row) => ({
          material_id: row.material_id ? row.material_id.toString() : "",
          template_details: this.selectedTemplates.map((tpl) => ({
            template_id: tpl.id ? tpl.id.toString() : "",
            supply_price: row.template_prices[tpl.id] || "",
          })),
        }));
      }

      // 立即执行时清空预约时间
      if (this.form.execute_mode == 0) {
        this.form.schedule_time = "";
      }

      const submitData = {
        type: this.form.type,
        memo: this.form.memo,
        schedule_time: this.form.schedule_time || "",
        task_details: task_details,
      };

      if (this.isEdit) {
        submitData.id = this.$route.params.id;
      }

      this.loading = true;
      try {
        const res = this.isEdit
          ? await TemplatePriceAdjustTasks.edit(this.$route.params.id, submitData)
          : await TemplatePriceAdjustTasks.add(submitData);
        if (res.data.status.isOK()) {
          this.$snackbar.success(
            i18n.t("TemplatePriceAdjustTasks.Messages.saveSuccess")
          );
          this.$router.push("/template-price-adjust-tasks");
        }
      } catch (e) {
        console.error("保存失败", e);
      }
      this.loading = false;
    },
  },

  watch: {
    // 切换任务类型时清空表格和模板选择
    "form.type": {
      handler(newVal) {
        if (newVal == 1) {
          // 切换到全模板，清空模板选择和模板价格
          this.selectedTemplates = [];
          this.tableData.forEach((row) => {
            row.template_prices = {};
            row.template_disabled = {};
          });
        } else {
          // 切换到分模板，清空全模板价格
          this.tableData.forEach((row) => {
            row.supply_price = "";
          });
        }
      },
    },
    // 切换执行方式时清空预约时间
    "form.execute_mode": {
      handler(newVal) {
        if (newVal == 0) {
          this.form.schedule_time = "";
        }
      },
    },
  },
};
</script>
